<template>
  <div v-if="adCampaign">
    <div class="vx-col w-full">
      <div class="bi-campaign--info-box">
        <h3 class="mb-5">Stellenvergleich</h3>
        <div class="vx-row">
          <div class="vx-col w-full lg:w-1/2">
            <VacancyCompareChart
              :numbers="vacancies.map(el => el.number)"
              metric="VISITORS"
              header="Besucher der letzten Woche"
              :source-ids="[
                '5197bc42-4304-45e7-8fde-4a1fedd742ac' /*Facebook*/,
                '281587b0-9a0c-40e4-8014-6b701f8b3734' /*Instagram*/
              ]"
              class="mb-5"
            />
          </div>
          <div class="vx-col w-full lg:w-1/2">
            <VacancyCompareChart
              :numbers="vacancies.map(el => el.number)"
              metric="APPLICATIONS_SENT"
              header="Bewerbungen der letzten Woche"
              :source-ids="[
                '5197bc42-4304-45e7-8fde-4a1fedd742ac' /*Facebook*/,
                '281587b0-9a0c-40e4-8014-6b701f8b3734' /*Instagram*/
              ]"
              class="mb-5"
              @chartDataSet="
                values =>
                  $emit(
                    'applicantNumberSet',
                    values.reduce(
                      (accumulator, currentValue) => accumulator + currentValue
                    )
                  )
              "
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-for="facebookAd of adCampaign.facebookAdInsights"
      :key="facebookAd.id"
      class="bi-campaign--info-box"
    >
      <h3>{{ facebookAd.adName }}</h3>
      <table class="important-informations mb-8">
        <thead>
          <tr>
            <th>Klicks</th>
            <th>Impressionen</th>
            <th>Kosten pro Klick</th>
            <th>Ausgegebener Betrag</th>
            <th>CTR</th>
            <th>Start</th>
            <th>Ende</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ facebookAd.clicks.toLocaleString() }}</td>
            <td>{{ facebookAd.impressions.toLocaleString() }}</td>
            <td>
              {{
                facebookAd.cpC.toLocaleString(undefined, {
                  style: "currency",
                  currency: "EUR"
                })
              }}
            </td>
            <td>
              {{
                facebookAd.spend.toLocaleString(undefined, {
                  style: "currency",
                  currency: "EUR"
                })
              }}
            </td>
            <td>
              {{
                facebookAd.clicks /
                  (facebookAd.impressions
                    ? facebookAd.impressions
                    : 1
                  ).toLocaleString()
              }}
            </td>
            <td>{{ $d(new Date(facebookAd.dateStart), "short") }}</td>
            <td>{{ $d(new Date(facebookAd.dateStop), "short") }}</td>
          </tr>
        </tbody>
      </table>
      <div class="vx-row">
        <div class="vx-col w-full lg:w-1/2">
          <FacebookStat :id="facebookAd.id"></FacebookStat>
        </div>
        <div class="vx-col w-full lg:w-1/2">
          <FacebookStatCosts :id="facebookAd.id"></FacebookStatCosts>
        </div>
      </div>
      <div class="vx-row">
        <div
          v-if="facebookAd.conversions.length"
          class="vx-col w-full lg:w-1/4"
        >
          <div class="bi-campaign--info-table">
            <h4>Conversions</h4>
            <table>
              <tr
                v-for="convertion of facebookAd.conversions"
                :key="convertion.id"
              >
                <th>{{ $t("message." + convertion.actionType) }}</th>
                <td>{{ convertion.value }}</td>
              </tr>
            </table>
          </div>
        </div>
        <div v-if="facebookAd.actions.length" class="vx-col w-full lg:w-1/4">
          <div class="bi-campaign--info-table">
            <h4>Aktionen</h4>
            <table>
              <tr v-for="action of facebookAd.actions" :key="action.id">
                <th>{{ $t("message." + action.actionType) }}</th>
                <td>{{ action.value }}</td>
              </tr>
            </table>
          </div>
        </div>
        <div
          v-if="facebookAd.actionValues.length"
          class="vx-col w-full lg:w-1/4"
        >
          <div class="bi-campaign--info-table">
            <h4>actionValues</h4>
            <table>
              <tr
                v-for="actionValue of facebookAd.actionValues"
                :key="actionValue.id"
              >
                <th>{{ $t("message." + actionValue.actionType) }}</th>
                <td>{{ actionValue.value }}</td>
              </tr>
            </table>
          </div>
        </div>
        <div
          v-if="facebookAd.costPerActionType.length"
          class="vx-col w-full lg:w-1/4"
        >
          <div class="bi-campaign--info-table">
            <h4>Kosten pro Aktion</h4>
            <table>
              <tr
                v-for="costPerActionType of facebookAd.costPerActionType"
                :key="costPerActionType.id"
              >
                <th>{{ $t("message." + costPerActionType.actionType) }}</th>
                <td>
                  {{ costPerActionType.value.toLocaleString(undefined, {}) }} €
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import FacebookStat from "./stats/FacebookStat";
import FacebookStatCosts from "./stats/FacebookStatCosts";
import VacancyCompareChart from "../../stats/VacancyCompareChart";

export default {
  components: {
    FacebookStat,
    FacebookStatCosts,
    VacancyCompareChart
  },
  props: {
    campaignId: { type: String, required: true },
    vacancies: { type: Array, required: true }
  },
  emits: ["applicantNumberSet"],
  data() {
    return {
      datacollection: null
    };
  },
  apollo: {
    adCampaign: {
      query: gql`
        query adCampaign($id: ID!) {
          adCampaign(id: $id) {
            id
            facebookAdInsights {
              id
              adName
              campaignName
              clicks
              impressions
              spend
              cpC
              dateStart
              dateStop
              conversions {
                id
                actionType
                value
              }
              actions {
                id
                actionType
                value
              }
              actionValues {
                id
                actionType
                value
              }
              costPerActionType {
                id
                actionType
                value
              }
            }
          }
        }
      `,
      variables() {
        return {
          id: this.campaignId
        };
      }
    }
  }
};
</script>
